import moment from 'moment';

import { Injectable }       from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }          from '../api/api.service';
import { AvatarService }       from '../core/services/avatar.service';
import { StreamSearchService } from '../api/stream-search.service';

import { BookingDiaryEventModel } from './typings/booking-diary.event';

import {
  BookingDiaryAllBookingModel,
  BookingDiaryEventTimeModel,
  BookingDiaryStatsModel,
  BookingDiaryUnassignedBacklogModel,
  BookingDiaryUnassignedJobModel
} from './typings/booking-diary.backlog';

import {
  BookingDiaryCustomerModel,
  BookingDiaryEventItemModel,
  BookingDiaryProductModel,
  BookingDiaryVehicleModel,
  BookingDiaryVehicleSearchModel
} from './typings/booking-diary.customer';

import {
  BookingDiaryBookingDraftModel,
  BookingDiaryNewVehicleModel
} from './typings/booking-diary.draft';

import {
  BookingDiaryGroupModel,
  BookingDiaryMechanicModel
} from './typings/booking-diary.mechanic';

import {
  BookingDiaryMonthMechanicModel,
  BookingDiaryMonthTotalsModel
} from './typings/booking-diary.month';

import {
  DATE_FORMAT,
  formatMinutes,
  minutesFromLocal,
  offsetFromLocal,
  SLOT_MINUTES
} from './booking-diary.layout';

const ALL_BOOKINGS_PAGE    : number = 200;
const DEFAULT_BODY_TYPE    : string = 'SEDAN';
const DEFAULT_TRANSMISSION : string = 'N/A';
const LABOUR_PRODUCT_TYPE  : string = 'J';
const MINUTES_IN_DAY       : number = 1440;
const REQUEST_PAGE_SIZE    : number = 500;
const UNASSIGNED_PAGE_SIZE : number = 100;
const VEHICLE_PAGE_SIZE    : number = 100;

// Every diary API call, answered in the diary's own shapes so the screens hold no API knowledge
@Injectable({
  providedIn: 'root'
})
export class BookingDiaryService {
  constructor (
    private apiService          : ApiService,
    private avatarService       : AvatarService,
    private streamSearchService : StreamSearchService,
    private translateService    : TranslateService
  ) {}

  addAppointment ( eventId : string, eventItemId : string, hours : number, date : string ) : Promise<any> {
    return this.apiService.post('/event_times', {
      estimated_hours: hours,
      event_date     : date,
      event_id       : eventId,
      event_item_id  : eventItemId,
      mechanic_id    : null
    }, 'event_time');
  }

  bookUnassignedJob (
    job          : BookingDiaryUnassignedJobModel,
    date         : string,
    startMinutes : number,
    mechanicId   : string
  ) : Promise<any> {
    return this.apiService.patch('/event_time', {
      estimated_hours : job.estimated_hours,
      event_id        : job.event_id,
      event_item_id   : job.event_item_id,
      id              : job.id,
      local_start_time: `${ date }T${ formatMinutes(startMinutes) }${ moment().format('Z') }`,
      mechanic_id     : mechanicId
    }, 'event_time');
  }

  // the booking as typed, shown on the grid while the saved one is read back; it has no id to open
  bookingFromDraft ( draft : BookingDiaryBookingDraftModel, mechanicName : string ) : BookingDiaryEventModel {
    const customer = draft.customer
      ? draft.customer.display_name
      : [ draft.newCustomer.company_name, draft.newCustomer.first_name, draft.newCustomer.last_name ]
        .filter(part => !!part)
        .join(' ');
    const vehicle  = draft.vehicle || draft.newVehicle;

    return {
      color              : this.avatarService.colorFor(String(draft.mechanicId)),
      customerDisplayName: customer,
      date               : draft.date,
      description        : draft.description,
      displayName        : mechanicName,
      endMinutes         : draft.startMinutes + Math.max(draft.hours * 60, SLOT_MINUTES),
      estimatedHours     : draft.hours,
      id                 : `draft-${ Date.now() }`,
      kind               : 'booking',
      lane               : 0,
      lanes              : 1,
      mechanicId         : String(draft.mechanicId),
      offset             : moment().format('Z'),
      startMinutes       : draft.startMinutes,
      vehicleDescription : vehicle
        ? this.buildVehicleDescription(vehicle)
        : ''
    };
  }

  cancelAppointment ( id : string ) : Promise<any> {
    return this.apiService.delete('/event_time', id);
  }

  // a typed-in customer and vehicle are created first, then the booking, placed as it is made; the
  // labour product it needs is read meanwhile rather than after
  createBooking ( draft : BookingDiaryBookingDraftModel ) : Promise<any> {
    const labour = this.getDefaultLabour();

    return this.resolveCustomer(draft)
      .then(customer => this.resolveVehicle(draft, customer)
        .then(vehicle => labour.then(product => this.createEvent(draft, customer, vehicle, product))));
  }

  // a vehicle for the customer from the words typed; the body type and transmission take the defaults
  createVehicle (
    customerId : string,
    newVehicle : BookingDiaryNewVehicleModel
  ) : Promise<BookingDiaryVehicleModel> {
    return this.apiService.post('/vehicles', {
      body_type   : DEFAULT_BODY_TYPE,
      color       : newVehicle.color,
      customer_id : customerId,
      make        : newVehicle.make,
      model       : newVehicle.model,
      plate_number: newVehicle.plate_number,
      transmission: DEFAULT_TRANSMISSION
    }, 'vehicle');
  }

  // the whole booking, not one appointment; invoices raised from it are unlinked, not removed
  deleteBooking ( id : string ) : Promise<any> {
    return this.apiService.delete('/event', id);
  }

  // the events index filters by date only when the segment parses as one, so 'all' lists everything
  getAllBookings (
    offset     : number = 0,
    pageSize   : number = ALL_BOOKINGS_PAGE,
    mechanicId : string = ''
  ) : Promise<Array<BookingDiaryAllBookingModel>> {
    return this.apiService.get('/events', [ 'all', offset, pageSize, 'event_date', 'desc' ], null, {
      query: { mechanic_id: mechanicId }
    })
      .then(( events : Array<BookingDiaryAllBookingModel> ) => events || []);
  }

  getAllBookingsCount ( mechanicId : string = '' ) : Promise<number> {
    return this.apiService.get('/events/count', 'all', null, {
      query: { mechanic_id: mechanicId }
    })
      .then(( count : any ) => parseInt(count, 10) || 0);
  }

  getBookingRequestCount () : Promise<number> {
    return this.apiService.get('/event_requests/count');
  }

  // no per-day request endpoint exists, so the pending list is bucketed by date; a failure is empty
  getBookingRequestDates () : Promise<{ [date : string] : number }> {
    return this.apiService.get('/event_requests', [ 0, REQUEST_PAGE_SIZE ])
      .then(( requests : Array<any> ) => {
        const dates : { [date : string] : number } = {};

        ( requests || [] ).forEach(request => {
          const date = String(request.requested_date || '').slice(0, 10);

          if ( date ) {
            dates[date] = ( dates[date] || 0 ) + 1;
          }
        });

        return dates;
      })
      .catch(() => ( {} ));
  }

  getCompany () : Promise<any> {
    return this.apiService.get('/company');
  }

  getCustomer ( id : string ) : Promise<BookingDiaryCustomerModel> {
    return this.apiService.get('/customer', id);
  }

  getCustomerVehicles ( customerId : string ) : Promise<Array<BookingDiaryVehicleModel>> {
    return this.apiService.get('/customers/vehicle_list_with_find', [
      customerId,
      '*',
      0,
      VEHICLE_PAGE_SIZE,
      'plate_number',
      'asc'
    ])
      .then(( vehicles : Array<BookingDiaryVehicleModel> ) => vehicles || []);
  }

  // the bookings alone, for a refresh after a save; the blocks around them have not moved
  getDayBookings (
    date      : string,
    mechanics : Array<BookingDiaryMechanicModel>
  ) : Promise<Array<BookingDiaryEventModel>> {
    return this.apiService.post('/event_times/bookings_for_day', {
      date,
      mechanic_ids: mechanics.map(mechanic => mechanic.id)
    })
      .then(bookings => this.normaliseBookings(bookings));
  }

  getDayEvents ( date : string, mechanics : Array<BookingDiaryMechanicModel> ) : Promise<Array<BookingDiaryEventModel>> {
    const params = {
      date,
      mechanic_ids: mechanics.map(mechanic => mechanic.id)
    };

    return Promise.all([
      this.apiService.post('/event_times/bookings_for_day', params),
      this.apiService.post('/unavailable_times/unavailable_for_day', params),
      this.apiService.post('/unscheduled_hours/unscheduled_for_day', params)
    ])
      .then(([ bookings, unavailable, unscheduled ]) => [
        ...this.normaliseBookings(bookings),
        ...this.normaliseBlocks(unavailable, 'unavailable'),
        ...this.normaliseBlocks(unscheduled, 'unscheduled')
      ]);
  }

  getEvent ( id : string ) : Promise<any> {
    return this.apiService.get('/event', id);
  }

  // with a date the groups hold who is rostered or booked that day; without one, every mechanic
  getGroups ( date : string, records : number ) : Promise<Array<BookingDiaryGroupModel>> {
    return this.apiService.get('/system/mechanic_grouping_for_filter', date ? [ records, date ] : String(records))
      .then(( raw : Array<any> ) => ( raw || [] ).map(( entry, index ) => {
        const group                                       = entry[index] || {};
        const mechanics : Array<BookingDiaryMechanicModel> = [];

        ( group.mechanic_ids || [] ).forEach(( mechanic : any ) => {
          Object.keys(mechanic).forEach(id => {
            mechanics.push({
              id,
              name: mechanic[id]
            });
          });
        });

        return {
          mechanics,
          name: group.group_name
        };
      }));
  }

  // the colour of the mechanic on the labour line's latest placed part, '' while none is placed
  getItemMechanicColor ( eventItemId : string ) : Promise<string> {
    return this.apiService.get('/event_times/index', eventItemId)
      .then(( eventTimes : Array<BookingDiaryEventTimeModel> ) => {
        const placed = ( eventTimes || [] )
          .filter(eventTime => !!eventTime.mechanic_id && eventTime.deleted !== true && eventTime.deleted !== 't')
          .sort(( a, b ) => ( parseFloat(String(b.ordering)) || 0 ) - ( parseFloat(String(a.ordering)) || 0 ))[0];

        return placed
          ? this.avatarService.colorFor(String(placed.mechanic_id))
          : '';
      });
  }

  getMonthTotals (
    date      : string,
    mechanics : Array<BookingDiaryMechanicModel>
  ) : Promise<{ [date : string] : BookingDiaryMonthTotalsModel }> {
    return this.apiService.post('/event_times/bookings_for_month', {
      mechanic_ids: mechanics.map(mechanic => mechanic.id),
      month       : moment(date, DATE_FORMAT).format('M'),
      year        : moment(date, DATE_FORMAT).format('YYYY')
    })
      .then(( response : any ) => {
        const totals : { [date : string] : BookingDiaryMonthTotalsModel } = {};

        Object.keys(response || {}).forEach(day => {
          const entry = response[day] || {};

          // the nil-keyed bucket holds the day's unassigned appointments: a count, not a mechanic
          const unassignedBucket = entry[''] || entry['null'];

          // the totals count the unassigned bucket as booked; the day view does not, nor does this
          const unassignedJobs  = unassignedBucket
            ? parseFloat(unassignedBucket.number_of_jobs) || 0
            : 0;
          const unassignedHours = unassignedBucket
            ? this.roundHours(unassignedBucket.booked_hours)
            : 0;

          totals[day] = {
            availableHours     : this.roundHours(this.flatOrTotal(entry, 'available_hours')),
            bookedHours        : Math.max(this.roundHours(this.flatOrTotal(entry, 'booked_hours')) - unassignedHours, 0),
            hasAvailableLoanCar: entry.available_loan_vehicles === true,
            jobs               : Math.max(( parseFloat(this.flatOrTotal(entry, 'number_of_jobs')) || 0 ) - unassignedJobs, 0),
            mechanics          : this.monthMechanicsFrom(entry, mechanics.map(mechanic => mechanic.id)),
            unassigned         : unassignedJobs
          };
        });

        return totals;
      });
  }

  getStats ( date : string ) : Promise<BookingDiaryStatsModel> {
    return this.apiService.get('/event_times/stats_for_day', date)
      .then(data => ( data && data[date] ) || {});
  }

  // one row over the page is asked for, so the caller can say the backlog goes on
  getUnassignedJobs (
    pageSize : number = UNASSIGNED_PAGE_SIZE,
    offset   : number = 0
  ) : Promise<BookingDiaryUnassignedBacklogModel> {
    return this.apiService.get('/events/unassigned_backlog', [ pageSize + 1, offset ])
      .then(( jobs : Array<BookingDiaryUnassignedJobModel> ) => ( {
        hasMore: ( jobs || [] ).length > pageSize,
        jobs   : ( jobs || [] ).slice(0, pageSize)
      } ));
  }

  getVehicle ( id : string ) : Promise<BookingDiaryVehicleModel> {
    return this.apiService.get('/vehicle', id);
  }

  getWeekBookings (
    date      : string,
    mechanics : Array<BookingDiaryMechanicModel>
  ) : Promise<Array<BookingDiaryEventModel>> {
    return this.apiService.post('/event_times/bookings_for_week', {
      date,
      mechanic_ids: mechanics.map(mechanic => mechanic.id)
    })
      .then(bookings => this.normaliseBookings(bookings));
  }

  getWeekEvents ( date : string, mechanics : Array<BookingDiaryMechanicModel> ) : Promise<Array<BookingDiaryEventModel>> {
    const params = {
      date,
      mechanic_ids: mechanics.map(mechanic => mechanic.id)
    };

    // a week with no unrostered time read is still a week; it just goes unshaded
    return Promise.all([
      this.apiService.post('/event_times/bookings_for_week', params),
      this.apiService.post('/unavailable_times/unavailable_for_week', params),
      this.apiService.post('/unscheduled_hours/unscheduled_for_week', params)
        .catch(() => null)
    ])
      .then(([ bookings, unavailable, unscheduled ]) => [
        ...this.normaliseBookings(bookings),
        ...this.normaliseBlocks(unavailable, 'unavailable'),
        ...this.normaliseBlocks(unscheduled, 'unscheduled')
      ]);
  }

  // a fresh read of the mechanic's day, so a slot booked elsewhere since the diary loaded is caught
  isSlotFree ( date : string, mechanicId : string, startMinutes : number, endMinutes : number ) : Promise<boolean> {
    return this.apiService.post('/event_times/bookings_for_day', { date, mechanic_ids: [ mechanicId ] })
      .then(bookings => !this.normaliseBookings(bookings).some(event => event.date === date
        && event.mechanicId === mechanicId
        && event.startMinutes < endMinutes
        && event.endMinutes > startMinutes));
  }

  rescheduleEvent (
    event        : BookingDiaryEventModel,
    date         : string,
    startMinutes : number,
    mechanicId   : string
  ) : Promise<any> {
    const booking = {
      date,
      id              : event.id,
      local_start_time: `${ date }T${ formatMinutes(startMinutes) }${ event.offset }`,
      mechanic_id     : mechanicId
    };

    const endMinutes = startMinutes + event.endMinutes - event.startMinutes;

    return event.kind === 'unavailable'
      ? this.apiService.patch('/unavailable_time', { unavailable_times: [ {
        ...booking,
        local_end_time: `${ date }T${ formatMinutes(endMinutes) }${ event.offset }`
      } ] })
      : this.apiService.patch('/event_time', booking, 'event_time');
  }

  // the end time derives from the estimated hours, so a length change is written through the hours
  resizeBooking ( event : BookingDiaryEventModel, date : string, endMinutes : number ) : Promise<any> {
    return this.apiService.patch('/event_time', {
      estimated_hours : ( endMinutes - event.startMinutes ) / 60,
      id              : event.id,
      local_start_time: `${ date }T${ formatMinutes(event.startMinutes) }${ event.offset }`,
      mechanic_id     : event.mechanicId
    }, 'event_time');
  }

  searchCustomers ( typed : string ) : Promise<Array<BookingDiaryCustomerModel>> {
    return Promise.resolve(this.streamSearchService.search('customers', typed))
      .then(( customers : Array<BookingDiaryCustomerModel> ) => customers || []);
  }

  searchLabourProducts ( typed : string ) : Promise<Array<BookingDiaryProductModel>> {
    return Promise.resolve(this.streamSearchService.search('products', typed))
      .then(( products : Array<BookingDiaryProductModel> ) => ( products || [] )
        .filter(product => product.product_type === LABOUR_PRODUCT_TYPE));
  }

  searchVehicles ( typed : string ) : Promise<Array<BookingDiaryVehicleSearchModel>> {
    return Promise.resolve(this.streamSearchService.search('vehicles', typed))
      .then(( vehicles : Array<BookingDiaryVehicleSearchModel> ) => vehicles || []);
  }

  unassignBooking ( event : BookingDiaryEventModel, date : string ) : Promise<any> {
    return this.apiService.patch('/event_time', {
      id              : event.id,
      local_start_time: `${ date }T${ formatMinutes(event.startMinutes) }${ event.offset }`,
      mechanic_id     : null
    }, 'event_time');
  }

  // the hours alone, for an appointment still unscheduled: it has no time or mechanic to send
  updateAppointmentHours ( id : string, hours : number ) : Promise<any> {
    return this.apiService.patch('/event_time', {
      id,
      estimated_hours: hours
    }, 'event_time');
  }

  updateAppointmentProduct ( eventId : string, item : BookingDiaryEventItemModel, product : BookingDiaryProductModel ) : Promise<any> {
    return this.apiService.patch('/event', {
      event_items_attributes: [ {
        description : product.description,
        gst_free    : product.gst_free,
        id          : item.id,
        item_code   : product.item_code,
        mechanic_id : item.mechanic_id,
        ordering    : item.ordering,
        product_id  : product.id,
        product_type: LABOUR_PRODUCT_TYPE,
        quantity    : item.quantity,
        unit_cost   : product.cost,
        unit_price  : product.retail_price
      } ],
      id                    : eventId
    }, 'event');
  }

  updateEventVehicle ( eventId : string, vehicleId : string ) : Promise<any> {
    return this.apiService.patch('/event', {
      id        : eventId,
      vehicle_id: vehicleId || ''
    }, 'event');
  }

  private buildDescription ( eventTime : any ) : string {
    return [ eventTime.description, eventTime.item_description ]
      .filter(part => !!part)
      .map(part => String(part).replace(/&#34;/g, '"'))
      .join(' - ');
  }

  private buildVehicleDescription ( eventTime : any ) : string {
    return [ eventTime.make, eventTime.model, eventTime.plate_number ]
      .filter(part => !!part)
      .join(' ');
  }

  private createEvent (
    draft    : BookingDiaryBookingDraftModel,
    customer : BookingDiaryCustomerModel,
    vehicle  : BookingDiaryVehicleModel,
    labour   : BookingDiaryProductModel
  ) : Promise<any> {
    return this.apiService.post('/events', {
      customer_id           : customer.id,
      description           : draft.description,
      discount              : 0,
      discount_type         : 'C',
      event_date            : draft.date,
      event_items_attributes: [ {
        description     : labour.description,
        gst_free        : labour.gst_free,
        item_code       : labour.item_code,
        local_start_time: `${ draft.date }T${ formatMinutes(draft.startMinutes) }${ moment().format('Z') }`,
        mechanic_id     : draft.mechanicId,
        ordering        : 0,
        product_id      : labour.id,
        product_type    : LABOUR_PRODUCT_TYPE,
        quantity        : draft.hours,
        unit_cost       : labour.cost,
        unit_price      : labour.retail_price
      } ],
      vehicle_id            : vehicle
        ? vehicle.id
        : ''
    }, 'event');
  }

  // a company shown as one shop gets flat day figures, not total_* ones beside its mechanics
  private flatOrTotal ( entry : any, key : string ) : any {
    return entry['total_' + key] !== undefined
      ? entry['total_' + key]
      : entry[key];
  }

  // a booking is priced from the default labour product; without one there is nothing to book
  private getDefaultLabour () : Promise<BookingDiaryProductModel> {
    return this.apiService.get('/products/default_labor_item')
      .then(( labour : BookingDiaryProductModel ) => labour && labour.id
        ? labour
        : Promise.reject(this.translateService.instant('BOOKING_SPACE.DIARY.NO_DEFAULT_LABOUR_PRODUCT')));
  }

  // mechanics keyed by id beside the day rollups; passed ids first so each keeps its place
  private monthMechanicsFrom ( entry : any, mechanicIds : Array<string> ) : Array<BookingDiaryMonthMechanicModel> {
    const day = entry || {};

    const isMechanic = ( id : string ) : boolean => {
      const value = day[id];

      if ( !value || typeof value !== 'object' || Array.isArray(value) ) {
        return false;
      }

      return 'display_name' in value || 'booked_hours' in value;
    };

    const ordered = ( mechanicIds || [] ).filter(id => isMechanic(id));
    const extras  = Object.keys(day)
      .filter(id => id !== '' && id !== 'null')
      .filter(id => isMechanic(id) && ordered.indexOf(id) === -1);

    return ordered.concat(extras).map(id => {
      const value          = day[id];
      const availableHours = this.roundHours(value.available_hours);
      const bookedHours    = this.roundHours(value.booked_hours);

      return {
        availableHours,
        bookedHours,
        id,
        color      : this.avatarService.colorFor(id || value.display_name),
        initials   : this.avatarService.initialsFor(value.display_name),
        jobs       : parseFloat(value.number_of_jobs) || 0,
        name       : value.display_name || '',
        utilisation: this.utilisationOf(value.percentage, bookedHours, availableHours)
      };
    });
  }

  private normaliseBlocks ( response : any, kind : 'unavailable' | 'unscheduled' ) : Array<BookingDiaryEventModel> {
    const events : Array<BookingDiaryEventModel> = [];
    const fallbackOffset                         = moment().format('Z');

    Object.keys(response || {}).forEach(date => {
      const day = response[date] || {};

      Object.keys(day).forEach(mechanicId => {
        ( day[mechanicId] || [] ).forEach(( block : any ) => {
          // an all-day entry carries no times and spans the day; an unscheduled gap carries no id
          const startMinutes = block.entire_day
            ? 0
            : minutesFromLocal(block.local_start_time);
          const endMinutes   = block.entire_day
            ? MINUTES_IN_DAY
            : minutesFromLocal(block.local_end_time);

          events.push({
            date,
            endMinutes,
            kind,
            startMinutes,
            displayName: block.display_name,
            id         : block.id
              ? String(block.id)
              : `${ kind }-${ mechanicId }-${ startMinutes }`,
            lane       : 0,
            lanes      : 1,
            mechanicId : String(block.mechanic_id || mechanicId),
            offset     : offsetFromLocal(block.local_start_time, fallbackOffset)
          });
        });
      });
    });

    return events;
  }

  private normaliseBookings ( response : any ) : Array<BookingDiaryEventModel> {
    const events : Array<BookingDiaryEventModel> = [];
    const fallbackOffset                         = moment().format('Z');

    Object.keys(response || {}).forEach(date => {
      const day = response[date] || {};

      Object.keys(day).forEach(mechanicId => {
        const mechanic = day[mechanicId];

        if ( !mechanic || Array.isArray(mechanic) ) {
          return;
        }

        ( mechanic.event_times || [] ).forEach(( eventTime : any ) => {
          // unassigned bookings ride along; with no start time they have no grid place
          if ( !eventTime.local_start_time ) {
            return;
          }

          const startMinutes = minutesFromLocal(eventTime.local_start_time);
          const endMinutes   = minutesFromLocal(eventTime.local_end_time);

          events.push({
            date,
            startMinutes,
            color              : this.avatarService.colorFor(String(eventTime.mechanic_id || mechanicId || mechanic.display_name)),
            customerDisplayName: eventTime.customer_display_name,
            customerId         : eventTime.customer_id,
            description        : this.buildDescription(eventTime),
            displayName        : mechanic.display_name,
            endMinutes         : Math.max(endMinutes, startMinutes + SLOT_MINUTES),
            estimatedHours     : eventTime.estimated_hours,
            eventId            : eventTime.event_id,
            eventItemId        : eventTime.event_item_id,
            id                 : String(eventTime.id),
            invoiceId          : eventTime.invoice_id,
            jobStatus          : eventTime.job_status,
            kind               : 'booking',
            lane               : 0,
            lanes              : 1,
            mechanicId         : String(eventTime.mechanic_id || mechanicId),
            offset             : offsetFromLocal(eventTime.local_start_time, fallbackOffset),
            ordering           : eventTime.ordering,
            totalSplits        : eventTime.total_splits,
            vehicleDescription : this.buildVehicleDescription(eventTime),
            vehicleId          : eventTime.vehicle_id
          });
        });
      });
    });

    return events;
  }

  private resolveCustomer ( draft : BookingDiaryBookingDraftModel ) : Promise<BookingDiaryCustomerModel> {
    if ( !draft.newCustomer ) {
      return Promise.resolve(draft.customer);
    }

    const isCompany = !!draft.newCustomer.company_name;

    return this.apiService.post('/customers', {
      cash_or_account: 'C',
      company_name   : draft.newCustomer.company_name,
      customer_type  : isCompany
        ? 'C'
        : 'I',
      first_name     : draft.newCustomer.first_name,
      last_name      : draft.newCustomer.last_name,
      mobile         : draft.newCustomer.mobile,
      price_type     : '1'
    }, 'customer');
  }

  private resolveVehicle (
    draft    : BookingDiaryBookingDraftModel,
    customer : BookingDiaryCustomerModel
  ) : Promise<BookingDiaryVehicleModel> {
    if ( !draft.newVehicle ) {
      return Promise.resolve(draft.vehicle);
    }

    return this.createVehicle(customer.id, draft.newVehicle);
  }

  private roundHours ( value : any ) : number {
    return Math.round(( parseFloat(value) || 0 ) * 10) / 10;
  }

  // the ring's title states the hours, so the share comes from them; the reported one is spare
  private utilisationOf ( percentage : any, bookedHours : number, availableHours : number ) : number {
    const capacity = bookedHours + Math.max(availableHours, 0);

    if ( capacity ) {
      return Math.min(Math.max(bookedHours / capacity, 0), 1);
    }

    const reported = parseFloat(percentage);

    return isNaN(reported)
      ? 0
      : Math.min(Math.max(reported, 0), 1);
  }
}
